import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import {
  CreditAnalyticsService,
  CreditBreakdownRow,
  CreditDimension,
  CreditFilters,
  CreditGranularity,
  CreditSummary,
  CreditTimeseriesPoint,
} from '../../services/credit-analytics.service';

/**
 * Credit-Verbrauch je Agent, Kurs, Aktion und Bereich (Migration 048).
 *
 * Die Seite gehoert dem KUNDEN: ein Kunden-Admin sieht seinen eigenen
 * Mandanten, super_admin jeden Mandanten oder alle zusammen. Die Grenze zieht
 * das Backend; hier wird nur entschieden, was angeboten wird — Kundenfilter,
 * Gruppierung „je Kunde" und der Abgleich gegen das Abrechnungsbuch sind
 * Plattform-Sicht. Wer die Seite ueberhaupt sieht: `credit-analytics.access.ts`.
 *
 * Attribution, NICHT Abrechnung. Drei Aussagen muss die Seite machen, sonst
 * fuehren die Zahlen in die Irre:
 *  - Zeilen ohne Agent/Kurs/Person werden als solche gezeigt, nie versteckt;
 *  - die Daten beginnen am Tag, an dem der Kanal ausgerollt wurde;
 *  - Credits summieren sich ueber eine Dimension, Nutzerzahlen NICHT (wer zwei
 *    Agenten nutzt, zaehlt bei beiden).
 */

/** Gleiche Presets und Beschriftung wie `app-date-range-control` auf den
 *  uebrigen Analytics-Seiten. Die Komponente selbst haengt am globalen
 *  AnalyticsService (Tutor-Filter, Retention-Clamp je Kundenplan) und passt
 *  hier nicht — uebernommen sind Optik und Takt-Regel. */
const DAY_PRESETS: { days: number; label: string }[] = [
  { days: 7, label: '7d' },
  { days: 30, label: '1M' },
  { days: 90, label: '3M' },
  { days: 365, label: '1Y' },
];
const MILLI = 1000;

type QuotaLevel = 'ok' | 'warn' | 'crit';

interface QuotaPool {
  key: 'plan' | 'purchase';
  used: number;
  total: number;
  pct: number;
  level: QuotaLevel;
}

interface QuotaSegment {
  key: string;
  credits: number;
  width: number;
}

/** Ab 75 % gelb, ab 90 % rot — die Farbe traegt die Dringlichkeit, damit man
 *  sie nicht erst aus der Zahl herauslesen muss. */
function levelFor(pct: number): QuotaLevel {
  if (pct >= 90) return 'crit';
  if (pct >= 75) return 'warn';
  return 'ok';
}

@Component({
  selector: 'app-credit-analytics',
  templateUrl: './credit-analytics.component.html',
  styleUrl: './credit-analytics.component.scss',
})
export class CreditAnalyticsComponent implements OnInit {
  readonly dayPresets = DAY_PRESETS;
  /** `area` fehlt bewusst: der Bereich hat seinen eigenen Donut und den
   *  Filter. `client` gibt es in einer Kundenansicht nicht. */
  readonly dimensions: CreditDimension[] = ['bot', 'course', 'activity', 'actor_kind', 'user'];
  /** Feste Liste statt aus den Daten: sonst schrumpfte das Dropdown auf den
   *  gerade gewaehlten Bereich und man kaeme nicht mehr zurueck. */
  readonly areas = ['chat', 'creator', 'parsing', 'sync', 'dozent', 'other'];

  /** Sentinel statt `null`: `nb-select` zeigt fuer den Wert `null` kein Label
   *  an, das Feld stand dann leer da. */
  readonly ALL = 'all';

  days = 30;
  dimension: CreditDimension = 'bot';
  botId = this.ALL;
  courseId = this.ALL;
  /** `all` oder ein Bereichsschluessel. */
  areaChoice = this.ALL;

  summary: CreditSummary | null = null;
  rows: CreditBreakdownRow[] = [];
  /** Who of the organisation uses the portal assistant how much (activity
   *  `portal_agent`, per person). Empty → the card is not shown. */
  agentRows: CreditBreakdownRow[] = [];
  private series: CreditTimeseriesPoint[] = [];
  private areaRows: CreditBreakdownRow[] = [];

  botOptions: CreditBreakdownRow[] = [];
  courseOptions: CreditBreakdownRow[] = [];

  // Abgeleitete Ansicht — EINMAL je Ladevorgang gebaut, nie im Getter.
  // Ein Getter mit frischem Objekt laeuft in jedem Change-Detection-Lauf, die
  // Charts saehen jedes Mal neue Inputs und renderten neu (CLAUDE.md Regel 18).
  areaDonutSeries: { name: string; data: any[] } = { name: 'credits', data: [] };
  dimensionDonutSeries: { name: string; data: any[] } = { name: 'credits', data: [] };
  timeseriesLabels: string[] = [];
  timeseriesSeries: { name: string; data: number[]; stack: string }[] = [];
  quotaPools: QuotaPool[] = [];
  quotaSegments: QuotaSegment[] = [];
  quotaTotal: { used: number; total: number; pct: number; level: QuotaLevel } | null = null;
  maxRowCredits = 0;

  // Kacheln. Vergleich gegen das gleich lange Fenster davor; null = kein
  // Vergleich moeglich (Vorzeitraum leer) — dann zeigt die Kachel nichts,
  // statt „+999 %".
  creditsChange: number | null = null;
  learnersChange: number | null = null;
  /** Ø Credits je eindeutigem Lerner des Kunden im Zeitraum — die Kennzahl
   *  der Lerner-Kachel; die Zahl der Lernenden steht darunter. */
  avgPerLearnerCredits = 0;
  avgPerLearnerChange: number | null = null;
  paymentsChange: number | null = null;
  /** Jetzt noch verfuegbar: Rest aus dem Tarif + nicht verbrauchte Zukaeufe. */
  availableCredits = 0;
  availableOf = 0;

  isLoading = false;
  loadFailed = false;

  /** Eine Nachkommastelle — ein Durchschnitt mit 15 Stellen sagt nichts
   *  Genaueres, er sieht nur kaputt aus. */
  readonly oneDecimal = (value: number): string =>
    (Math.round((value ?? 0) * 10) / 10).toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });

  readonly euroFormat = (value: number): string =>
    (value ?? 0).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    });

  readonly wholeNumber = (value: number): string =>
    Math.round(value ?? 0).toLocaleString('de-DE');

  /** Ohne Lernende im Zeitraum gibt es keinen Durchschnitt: „—" statt 0. */
  readonly avgPerLearnerFormat = (value: number): string =>
    this.summary?.learners ? this.oneDecimal(value) : '—';

  constructor(
    private creditAnalytics: CreditAnalyticsService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadOptions();
    this.load();
  }

  // ─── Filter ──────────────────────────────────────────────────────────────

  /** Takt aus dem Zeitraum, dieselbe Regel wie `app-date-range-control`:
   *  bis 30 Tage je Tag, 90 Tage je Woche, ein Jahr je Monat. Ein eigener
   *  Umschalter daneben war eine zweite Zeitachse fuer dieselbe Frage. */
  get granularity(): CreditGranularity {
    if (this.days <= 30) return 'day';
    if (this.days <= 90) return 'week';
    return 'month';
  }

  private get window(): { start: string; end: string } {
    const end = new Date();
    const start = new Date(end.getTime() - this.days * 24 * 60 * 60 * 1000);
    return { start: start.toISOString(), end: end.toISOString() };
  }

  private pick(value: string): string | undefined {
    return value === this.ALL ? undefined : value;
  }

  private get filters(): CreditFilters {
    const f: CreditFilters = {
      ...this.window,
      bot_id: this.pick(this.botId),
      course_id: this.pick(this.courseId),
    };
    if (this.areaChoice !== this.ALL) f.area = this.areaChoice;
    return f;
  }

  /** `nb-button-group` liefert ein ARRAY der gedrueckten Werte. Klickt jemand
   *  den bereits aktiven Knopf, ist es leer — ohne diese Pruefung stuende
   *  `days` auf `undefined` und der Zeitraum waere kaputt. */
  setDays(days: number | undefined): void {
    if (days === undefined || days === null || days === this.days) return;
    this.days = days;
    this.loadOptions();
    this.load();
  }

  setBot(id: string): void {
    this.botId = id;
    this.load();
  }

  setCourse(id: string): void {
    this.courseId = id;
    this.load();
  }

  setArea(choice: string): void {
    this.areaChoice = choice;
    this.load();
  }

  setDimension(dimension: CreditDimension): void {
    this.dimension = dimension;
    this.load();
  }

  get hasActiveFilter(): boolean {
    return [this.botId, this.courseId, this.areaChoice]
      .some((v) => v !== this.ALL);
  }

  resetFilters(): void {
    this.botId = this.courseId = this.areaChoice = this.ALL;
    this.load();
  }

  // ─── Load ────────────────────────────────────────────────────────────────

  /** Auswahllisten nur aus dem Zeitraum, nicht aus den uebrigen Filtern —
   *  sonst enthielte die Agentenliste nach einer Auswahl nur noch diesen
   *  einen Agenten. Zeilen ohne Agent/Kurs sind kein waehlbarer Wert. */
  private loadOptions(): void {
    const base: CreditFilters = { ...this.window };
    const real = (rows: CreditBreakdownRow[]) =>
      rows.filter((r) => r.key && !r.key.startsWith('__'));
    forkJoin({
      bots: this.creditAnalytics.breakdown('bot', base, 200),
      courses: this.creditAnalytics.breakdown('course', base, 200),
    }).subscribe({
      next: (res) => {
        this.botOptions = real(res.bots);
        this.courseOptions = real(res.courses);
      },
      error: () => {},
    });
  }

  load(): void {
    this.isLoading = true;
    this.loadFailed = false;
    const f = this.filters;
    forkJoin({
      summary: this.creditAnalytics.summary(f),
      rows: this.creditAnalytics.breakdown(this.dimension, f),
      // Nach Aktion, nicht nach Bereich: so ist im gestapelten Balken jede
      // Aktionsart (Rueckfrage, Debatte, Uebung, …) eine eigene Farbe.
      series: this.creditAnalytics.timeseries('activity', f, this.granularity),
      areaRows: this.creditAnalytics.breakdown('area', f),
      agentRows: this.creditAnalytics.breakdown('user', { ...f, activity_type: 'portal_agent' }),
    }).subscribe({
      next: (res) => {
        this.summary = res.summary;
        this.rows = res.rows.map((r) => ({ ...r, label: this.rowLabel(r) }));
        this.agentRows = res.agentRows;
        this.series = res.series;
        this.areaRows = res.areaRows;
        this.rebuildView();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.loadFailed = true;
      },
    });
  }

  private rebuildView(): void {
    this.maxRowCredits = this.rows.reduce((max, r) => Math.max(max, r.credits_milli), 0);

    // Donuts: nur Zeilen mit Credits — ein 0-Segment ist im Kreis unsichtbar
    // und in der Legende irrefuehrend.
    this.areaDonutSeries = {
      name: 'credits',
      data: this.areaRows
        .filter((r) => r.credits_milli > 0)
        .map((r) => ({ name: this.areaLabel(r.key), value: this.credits(r.credits_milli) })),
    };
    // Sieben groesste Zeilen, der Rest als „Übrige“ — ohne den Rest rechnete
    // ECharts die Anteile nur ueber die sichtbaren Segmente und blies sie auf.
    const withCredits = this.rows.filter((r) => r.credits_milli > 0 && r.key !== '__other__');
    const rest =
      withCredits.slice(7).reduce((sum, r) => sum + r.credits_milli, 0) +
      this.rows.filter((r) => r.key === '__other__').reduce((sum, r) => sum + r.credits_milli, 0);
    this.dimensionDonutSeries = {
      name: 'credits',
      data: [
        ...withCredits.slice(0, 7).map((r) => ({ name: r.label, value: this.credits(r.credits_milli) })),
        ...(rest > 0 ? [{ name: this.areaLabel('__other__'), value: this.credits(rest) }] : []),
      ],
    };

    // Verlauf: eine Serie je Aktion, gestapelt. `[stacked]` an app-bar-chart
    // ist deklariert, wird dort aber NICHT ausgewertet — ECharts stapelt nur
    // ueber `stack` je Serie.
    //
    // Achse aus dem ganzen Zeitraum, nicht aus den Daten: das Backend liefert
    // nur Tage MIT Verbrauch. Ohne Auffuellen fehlte ein Tag ohne Nutzung auf
    // der Achse, statt als 0 dazustehen — der Verlauf saehe lueckenlos aus.
    const buckets = this.bucketKeys();
    const byKey = new Map<string, number>();
    const names = new Map<string, string>();
    for (const p of this.series) {
      const key = (p.bucket || '').slice(0, 10);
      byKey.set(`${key}|${p.series}`, (byKey.get(`${key}|${p.series}`) ?? 0) + p.credits_milli);
      names.set(p.series, p.series === '__other__' ? this.areaLabel('__other__') : (p.label || p.series));
    }
    this.timeseriesLabels = buckets.map((b) => this.formatBucket(b));
    this.timeseriesSeries = Array.from(names.entries()).map(([key, label]) => ({
      name: label,
      stack: 'credits',
      data: buckets.map((b) => this.credits(byKey.get(`${b}|${key}`) ?? 0)),
    }));

    this.rebuildKpis();

    this.rebuildQuota();
  }

  private rebuildKpis(): void {
    const s = this.summary;
    const change = (cur: number, prev: number | null | undefined): number | null =>
      prev && prev > 0 ? Math.round(((cur - prev) / prev) * 100) : null;
    this.creditsChange = s ? change(s.credits_milli, s.previous?.credits_milli) : null;
    this.learnersChange = s ? change(s.learners, s.previous?.learners) : null;
    this.avgPerLearnerCredits =
      s?.avg_credits_milli_per_learner != null ? this.credits(s.avg_credits_milli_per_learner) : 0;
    const prevAvg = s?.previous?.avg_credits_milli_per_learner;
    this.avgPerLearnerChange =
      s?.avg_credits_milli_per_learner != null && prevAvg != null
        ? change(s.avg_credits_milli_per_learner, prevAvg)
        : null;
    this.paymentsChange = s ? change(s.payments_eur, s.previous?.payments_eur) : null;

    const q = s?.quota;
    const planLeft = q ? this.credits(q.remaining_credits_milli) : 0;
    const bought = q ? q.purchased_credits : 0;
    this.availableCredits = planLeft + bought;
    this.availableOf = (q?.included_credits ?? 0) + (q?.purchased_granted_credits ?? 0);
  }

  /** Alle Zeitpunkte des Fensters als `YYYY-MM-DD` (lokaler Kalendertag,
   *  wie das Backend in Europe/Berlin rechnet). Woche = Montag, Monat = 1. */
  private bucketKeys(): string[] {
    const pad = (n: number) => String(n).padStart(2, '0');
    const key = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const end = new Date();
    const cursor = new Date(end.getTime() - this.days * 24 * 60 * 60 * 1000);
    cursor.setHours(0, 0, 0, 0);
    if (this.granularity === 'week') {
      cursor.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
    } else if (this.granularity === 'month') {
      cursor.setDate(1);
    }
    const keys: string[] = [];
    while (cursor <= end) {
      keys.push(key(cursor));
      if (this.granularity === 'day') cursor.setDate(cursor.getDate() + 1);
      else if (this.granularity === 'week') cursor.setDate(cursor.getDate() + 7);
      else cursor.setMonth(cursor.getMonth() + 1);
    }
    return keys;
  }

  /** Zwei Toepfe, in dieser Reihenfolge verbraucht: erst der Tarif, dann die
   *  Zukaeufe (FIFO, `credit_limits.py`). Der Tarif-Verbrauch ist deshalb auf
   *  das Kontingent gedeckelt; was darueber hinausgeht, steht beim Zukauf —
   *  dort aus dem Ledger (gewaehrt − verbleibend), nicht geschaetzt.
   *  Der Gesamtbalken ist 100 % = Tarif + Zukauf, je Topf verbraucht (satt)
   *  und frei (hell). */
  private rebuildQuota(): void {
    const q = this.summary?.quota;
    const pools: QuotaPool[] = [];
    if (q && q.included_credits > 0) {
      const used = Math.min(this.credits(q.used_credits_milli), q.included_credits);
      pools.push(pool('plan', used, q.included_credits));
    }
    if (q && q.purchased_granted_credits > 0) {
      const used = Math.max(0, q.purchased_granted_credits - q.purchased_credits);
      pools.push(pool('purchase', used, q.purchased_granted_credits));
    }
    this.quotaPools = pools;

    const total = pools.reduce((sum, p) => sum + p.total, 0);
    if (!total) {
      this.quotaSegments = [];
      this.quotaTotal = null;
      return;
    }
    const used = pools.reduce((sum, p) => sum + p.used, 0);
    const pct = (used / total) * 100;
    this.quotaTotal = { used, total, pct, level: levelFor(pct) };
    this.quotaSegments = pools.flatMap((p) => [
      { key: `${p.key}Used`, credits: p.used, width: (p.used / total) * 100 },
      { key: `${p.key}Free`, credits: p.total - p.used, width: ((p.total - p.used) / total) * 100 },
    ]);
  }

  // ─── Display helpers ─────────────────────────────────────────────────────

  /** Credits are stored in MILLIcredits (1 credit = 1000) because an impulse
   *  costs 0.25 — rounding per booking would drift. Round only here. */
  credits(milli: number | null | undefined): number {
    return (milli ?? 0) / MILLI;
  }

  /** Net euro at the anchor rate (0.04 € = 25 credits/€, migration 035). */
  euro(milli: number | null | undefined): number {
    return this.credits(milli) * 0.04;
  }

  barWidth(row: CreditBreakdownRow): number {
    return this.maxRowCredits > 0 ? (row.credits_milli / this.maxRowCredits) * 100 : 0;
  }

  /** Rows the backend could not attribute to an agent are labelled
   *  "ohne Agent · <area>" — they must stay visible, otherwise the column no
   *  longer sums to the KPI card. */
  isUnattributed(row: CreditBreakdownRow): boolean {
    return row.key === '__none__';
  }

  trackRow(_: number, row: CreditBreakdownRow): string {
    // „ohne Agent“ gibt es je Bereich einmal — alle mit dem Schluessel `__none__`.
    return `${row.key}|${row.label}`;
  }

  /** Beschriftungen, die das Backend fest auf Deutsch liefert, in der Sprache
   *  der Oberflaeche — erkannt am Schluessel bzw. am festen Wortlaut. */
  private rowLabel(row: CreditBreakdownRow): string {
    const t = (key: string) => this.t.instant(`pages.analytics.creditAnalytics.${key}`);
    if (row.key === '__portal_agent__') return t('portalAgent.title');
    if (row.key === '__other__') return this.areaLabel('__other__');
    const unattributed = /^ohne Agent · (.+)$/.exec(row.label || '');
    if (unattributed) return `${t('labels.noAgent')} · ${this.areaLabel(unattributed[1])}`;
    const fixed: Record<string, string> = {
      'gelöschter Agent': 'labels.deletedAgent',
      'ohne Kurs': 'labels.noCourse',
      'gelöschter Kurs': 'labels.deletedCourse',
      'gelöschte Person': 'labels.deletedPerson',
    };
    return fixed[row.label] ? t(fixed[row.label]) : row.label;
  }

  get hasSeries(): boolean {
    return this.timeseriesSeries.length > 0;
  }

  /** Bei Monatsansicht nur Monat+Jahr, sonst Tagesdatum — ein ISO-Zeitstempel
   *  auf der Achse ist unlesbar. */
  private formatBucket(bucket: string): string {
    // `YYYY-MM-DD` als lokales Datum lesen — `new Date('2026-09-28')` waere
    // UTC-Mitternacht und in Berlin vormittags korrekt, westlich davon der Vortag.
    const [y, m, d0] = bucket.slice(0, 10).split('-').map(Number);
    const d = new Date(y, m - 1, d0);
    if (this.granularity === 'month') {
      return d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
    }
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  }

  /** Bereichsnamen fuer die Charts uebersetzen. Im Chart gibt es keine
   *  translate-Pipe, also hier aufloesen. `__other__` ist der Sammel-Eintrag
   *  des Backends fuer alles ausserhalb der Top-N; faellt die Uebersetzung
   *  aus, bleibt der rohe Schluessel stehen statt einer leeren Legende. */
  private areaLabel(key: string): string {
    const path = `pages.analytics.creditAnalytics.area.${key}`;
    const translated = this.t.instant(path);
    return translated === path ? key : translated;
  }
}

function pool(key: 'plan' | 'purchase', used: number, total: number): QuotaPool {
  const pct = total > 0 ? (used / total) * 100 : 0;
  return { key, used, total, pct, level: levelFor(pct) };
}
